import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { OrderQueueCard } from "@/components/workshop/OrderQueueCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { toast } from "sonner";
import { Search, Package, RefreshCw } from "lucide-react";

interface IntakeOrder {
  id: string;
  publicId: string;
  orderType: string;
  status: string;
  itemCount: number;
  items: { id: string; garmentType: string; qty: number; tagCode?: string }[];
  customer: { publicId: string; name: string; phone?: string };
  address: { line1: string; line2?: string; city: string; pincode: string };
  area: { name: string };
  pickupShipment?: {
    status: string;
    handoverTime?: string;
    slot: { startAt: string; endAt: string };
  };
  createdAt: string;
}

export default function IntakePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<IntakeOrder[]>([]);
  const [search, setSearch] = useState("");
  const [receiving, setReceiving] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);

      const response = await fetch(`/api/workshop/intake?${params}`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }

  async function handleReceive(orderId: string) {
    setReceiving(orderId);
    try {
      const response = await fetch(`/api/workshop/orders/${orderId}/receive`, {
        method: "POST",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to receive order");
      }

      toast.success("Order received at workshop");
      navigate(`/workshop/orders/${orderId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to receive order");
    } finally {
      setReceiving(null);
    }
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    fetchOrders();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Intake"
        actions={
          <Button variant="outline" onClick={fetchOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by order ID, customer ID, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No orders awaiting intake"
          description="Orders will appear here once riders hand them over at the workshop."
        />
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <OrderQueueCard
              key={order.id}
              order={order}
              action={{
                label: "Receive",
                onClick: () => handleReceive(order.id),
                loading: receiving === order.id,
              }}
              secondaryAction={{
                label: "View Details",
                onClick: () => navigate(`/workshop/orders/${order.id}`),
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
